import { ColDef, ValueFormatterParams } from "ag-grid-community";

import { Permission } from "../../contexts/permissions/fetchPermissions";
import { hasRequiredPermissions } from "../../hoc/withPermissions/helpers/hasRequiredPermissions";
import { Permissions } from "../../permissions";
import { Record } from "../../types";
import { Currency, formatCurrency } from "../../utils/formatCurrency";
import { ActionMenu } from "./ActionMenu";
import { StatusBadge } from "./StatusBadge";

const getColumns = (permissions: Permission[]) => {
  const hasModifyPermissions = hasRequiredPermissions(permissions, [
    Permissions.MarginReportsModify,
  ]);

  const columnDefs: ColDef<Record>[] = [];

  if (hasModifyPermissions) {
    columnDefs.push({
      headerName: "Actions",
      colId: "actionMenu",
      cellRenderer: (params: ValueFormatterParams<Record>) => {
        return (
          params?.data?.actionMenu && (
            <ActionMenu value={params.data.actionMenu} />
          )
        );
      },
      filter: false,
      sortable: false,
      width: 90,
    });
  }

  columnDefs.push(
    ...([
      {
        headerName: "Status",
        colId: "status",
        valueGetter: params => params.data?.status,
        cellRenderer: (params: ValueFormatterParams<Record>) => {
          return (
            params?.data?.status && <StatusBadge value={params.data.status} />
          );
        },
        filter: "agSetColumnFilter",
        width: 100,
      },
      {
        headerName: "Type",
        colId: "type",
        valueGetter: params => params.data?.type,
        width: 110,
        type: "enum",
      },
      {
        headerName: "Event ID",
        colId: "callId",
        valueGetter: params => params.data?.callId,
        filter: "agTextColumnFilter",
        width: 210,
        initialHide: true,
      },
      {
        headerName: "Corr. Code",
        colId: "correspondentCode",
        valueGetter: params => params.data?.correspondentCode,
        filter: "agTextColumnFilter",
        width: 130,
      },
      {
        headerName: "Account ID",
        colId: "accountId",
        valueGetter: params => params.data?.accountId,
        filter: "agTextColumnFilter",
        width: 130,
      },
      {
        headerName: "Account Holder",
        colId: "accountHolder",
        valueGetter: params => params.data?.accountHolder,
        filter: "agTextColumnFilter",
        width: 230,
      },
      {
        headerName: "Call Type",
        colId: "callType",
        valueGetter: params => params.data?.callType,
        filter: "agSetColumnFilter",
        width: 130,
      },
      {
        headerName: "Event Name",
        colId: "eventName",
        valueGetter: params => params.data?.eventName,
        filter: "agSetColumnFilter",
        width: 230,
      },
      {
        headerName: "Description",
        colId: "description",
        valueGetter: params => params.data?.description,
        filter: "agTextColumnFilter",
        width: 130,
      },
      {
        headerName: "Call Amount",
        colId: "callAmount",
        type: "rightAligned",
        valueGetter: params => params.data?.callAmount,
        valueFormatter: ({ value }) =>
          formatCurrency(Currency.Usd, value ? String(value) : null),
        filter: "agNumberColumnFilter",
        width: 150,
      },
      {
        headerName: "Trade Date",
        colId: "tradeDate",
        valueGetter: params => params.data?.tradeDate,
        type: "date",
        width: 130,
      },
      {
        headerName: "Due Date",
        colId: "dueDate",
        valueGetter: params => params.data?.dueDate,
        type: "date",
        width: 130,
      },
      {
        headerName: "Issue Date",
        colId: "issueDate",
        valueGetter: params => params.data?.issueDate,
        type: "date",
        width: 130,
      },
      {
        headerName: "Closed Date",
        colId: "closedDate",
        valueGetter: params => params.data?.closedDate,
        type: "date",
        width: 150,
      },
    ] as ColDef<Record>[])
  );

  return columnDefs;
};

export default getColumns;
